import { z } from 'zod';
import { WorkflowCategory } from './project.js';
/**
 * Replay — trạng thái dự án tại một thời điểm quá khứ (APM-901, spec mục 17).
 *
 * ## Vì sao có type riêng thay vì dùng lại `TaskView` / `GanttView`
 *
 * View "hiện tại" mang những thứ **chỉ có ở hiện tại**: tên người được phân
 * công (join sang `user`), URL đính kèm, số bình luận. Replay không có quyền
 * khẳng định những thứ đó ở quá khứ — tên có thể đã đổi, file có thể đã bị
 * xoá — nên nó trả về **id** và để phía gọi tự tra danh bạ hiện tại nếu muốn
 * hiện tên.
 *
 * Hệ quả cố ý: thiếu field nào là nói ra, không đoán. Xem `ReplayMeta.partial`.
 */
export const ReplayView = z.enum([
    'board',
    'gantt',
    'workload',
    'progress',
    'raid',
    'objectives',
    'documents',
]);
// ── Board ───────────────────────────────────────────────────────────────────
export const ReplayBoardCard = z.object({
    taskId: z.uuid(),
    key: z.string().nullable(),
    title: z.string(),
    assigneeId: z.uuid().nullable(),
    parentId: z.uuid().nullable(),
    progressPct: z.number().int().min(0).max(100),
    isBlocked: z.boolean(),
    estimateHours: z.number().nullable(),
});
export const ReplayBoardColumn = z.object({
    statusId: z.uuid().nullable(),
    name: z.string(),
    category: WorkflowCategory,
    cards: z.array(ReplayBoardCard),
});
export const ReplayBoard = z.object({ columns: z.array(ReplayBoardColumn) });
// ── Gantt ───────────────────────────────────────────────────────────────────
export const ReplayGanttBar = z.object({
    taskId: z.uuid(),
    key: z.string().nullable(),
    title: z.string(),
    parentId: z.uuid().nullable(),
    startDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    progressPct: z.number().int().min(0).max(100),
    isMilestone: z.boolean(),
    category: WorkflowCategory,
    dependsOn: z.array(z.uuid()),
});
export const ReplayGantt = z.object({
    bars: z.array(ReplayGanttBar),
    /** Task chưa từng được đặt ngày ở mốc này — Gantt không vẽ được, nhưng nói ra. */
    undated: z.number().int().nonnegative(),
});
// ── Workload ────────────────────────────────────────────────────────────────
export const ReplayWorkloadRow = z.object({
    /** `null` = chưa phân công. Luôn là hàng cuối. */
    userId: z.uuid().nullable(),
    openTasks: z.number().int().nonnegative(),
    openHours: z.number().nonnegative(),
    doneTasks: z.number().int().nonnegative(),
    blockedTasks: z.number().int().nonnegative(),
});
export const ReplayWorkload = z.object({
    rows: z.array(ReplayWorkloadRow),
    /** Thành viên dự án tại mốc đó — hàng có 0 task vẫn phải xuất hiện. */
    memberIds: z.array(z.uuid()),
});
// ── Tiến độ ─────────────────────────────────────────────────────────────────
export const ReplayProgress = z.object({
    taskCount: z.number().int().nonnegative(),
    doneCount: z.number().int().nonnegative(),
    inProgressCount: z.number().int().nonnegative(),
    blockedCount: z.number().int().nonnegative(),
    /** Bình quân **có trọng số theo giờ ước lượng** — task không có giờ tính trọng số 1. */
    progressPct: z.number().min(0).max(100),
    estimateHours: z.number().nonnegative(),
    doneHours: z.number().nonnegative(),
});
// ── RAID ────────────────────────────────────────────────────────────────────
export const ReplayRaidRow = z.object({
    raidId: z.uuid(),
    number: z.number().int().nullable(),
    type: z.string().nullable(),
    title: z.string(),
    status: z.string(),
    probability: z.number().int().nullable(),
    impact: z.number().int().nullable(),
    score: z.number().int().nullable(),
    /**
     * KHÔNG có `ownerId`, có chủ đích: `raid.created` và `raid.updated` không
     * mang người phụ trách, nên lịch sử không biết ai sở hữu rủi ro nào ở một
     * mốc quá khứ. Trả về `null` cho mọi hàng sẽ trông như "chưa ai phụ trách" —
     * một câu trả lời sai, khác hẳn với "không biết".
     */
});
export const ReplayRaid = z.object({
    items: z.array(ReplayRaidRow),
    openCount: z.number().int().nonnegative(),
    /** Tổng điểm rủi ro còn treo — cùng định nghĩa với risk burndown của APM-803. */
    exposure: z.number().int().nonnegative(),
});
// ── Objective ───────────────────────────────────────────────────────────────
export const ReplayKeyResult = z.object({
    keyResultId: z.uuid(),
    title: z.string(),
    targetValue: z.number().nullable(),
    currentValue: z.number().nullable(),
    unit: z.string().nullable(),
});
export const ReplayObjective = z.object({
    objectiveId: z.uuid(),
    title: z.string(),
    status: z.string().nullable(),
    keyResults: z.array(ReplayKeyResult),
    linkedTaskIds: z.array(z.uuid()),
});
export const ReplayObjectives = z.object({ objectives: z.array(ReplayObjective) });
// ── Tài liệu ────────────────────────────────────────────────────────────────
export const ReplayDocumentRow = z.object({
    documentId: z.uuid(),
    title: z.string(),
    type: z.string().nullable(),
    status: z.string(),
    version: z.number().int().nonnegative(),
});
export const ReplayDocuments = z.object({
    items: z.array(ReplayDocumentRow),
    byStatus: z.record(z.string(), z.number().int().nonnegative()),
});
// ── Vỏ chung ────────────────────────────────────────────────────────────────
/**
 * Xuất xứ của một lần replay — **luôn** trả kèm state.
 *
 * Không phải để debug: người dùng đang nhìn một con số về quá khứ và phải biết
 * nó được dựng từ đâu. `snapshotAt: null` + `eventsApplied` lớn nghĩa là lần
 * đọc này gấp lại từ event đầu tiên, và đó là lý do nó chậm.
 */
export const ReplayMeta = z.object({
    at: z.iso.datetime(),
    /** Mốc snapshot dùng làm điểm xuất phát; `null` = gấp từ event đầu tiên. */
    snapshotAt: z.iso.datetime().nullable(),
    /** Số event đã apply **sau** snapshot. */
    eventsApplied: z.number().int().nonnegative(),
    /** `event_log.id` cuối cùng đã gộp — hai lần đọc cùng giá trị này ra cùng state. */
    lastEventId: z.string(),
    tookMs: z.number().nonnegative(),
    /**
     * Field mà lịch sử **không** dựng lại được ở mốc này, dạng
     * `task.parentId`. Rỗng là đầy đủ; có phần tử là "chỗ này thiếu, đừng tin".
     *
     * Có vì event `task.created` trước APM-901 không mang state ban đầu đầy đủ,
     * và im lặng ở đây nghĩa là người dùng đọc một cây WBS phẳng rồi tin rằng dự
     * án lúc đó thật sự phẳng.
     */
    partial: z.array(z.string()),
});
export const ReplayStateResponse = z.object({
    meta: ReplayMeta,
    view: ReplayView,
    board: ReplayBoard.optional(),
    gantt: ReplayGantt.optional(),
    workload: ReplayWorkload.optional(),
    progress: ReplayProgress.optional(),
    raid: ReplayRaid.optional(),
    objectives: ReplayObjectives.optional(),
    documents: ReplayDocuments.optional(),
});
// ── Thanh trượt thời gian (APM-902, spec 17.4) ──────────────────────────────
/**
 * Loại marker trên thanh trượt.
 *
 * Mỗi loại là một **sự kiện có trong `event_log`**, không phải một suy diễn từ
 * bảng hiện tại: kéo thanh trượt về tháng trước phải thấy đúng những mốc mà
 * tháng trước đã có, kể cả khi sprint đó nay đã bị xoá.
 */
export const ReplayMarkerKind = z.enum([
    'sprint_started',
    'sprint_closed',
    'cr_approved',
    'risk_high',
    'milestone_set',
    'baseline',
    'phase_gate',
    /**
     * Lần một hạng mục **bị lùi hạn** — proxy sự kiện cho "bắt đầu trễ tiến độ"
     * của spec 17.4.
     *
     * Nói rõ nó là gì thay vì gọi nó là "trễ tiến độ": trễ theo nghĩa SPI cần
     * đường plan-vs-actual của từng ngày (đã có ở `APM-803`, và tính nó cho toàn
     * vòng đời chỉ để đặt marker là quá đắt cho một thanh trượt). Ngày bị lùi là
     * tín hiệu có thật, rẻ, và **nhìn thấy được** trên dòng event.
     */
    'due_pushed',
]);
export const ReplayMarker = z.object({
    at: z.iso.datetime(),
    kind: ReplayMarkerKind,
    /** Nhãn hiển thị, đã là tiếng Việt sẵn sàng đọc — vd "Sprint 3 bắt đầu". */
    label: z.string(),
    /** Aggregate sinh ra marker, để phía gọi mở chi tiết nếu muốn. */
    entityId: z.uuid().nullable(),
});
export const ReplayDensityBucket = z.enum(['day', 'week']);
export const ReplayDensityPoint = z.object({
    /** Đầu ô, `YYYY-MM-DD` theo UTC. */
    at: z.iso.date(),
    count: z.number().int().nonnegative(),
});
/**
 * Dữ liệu để **vẽ** thanh trượt, không phải để dựng state.
 *
 * Tách khỏi `stateAt` vì hai thứ có nhịp khác nhau hoàn toàn: thanh trượt tải
 * **một lần** khi mở màn hình, còn state được hỏi lại mỗi lần người dùng kéo.
 * Gộp chung nghĩa là mỗi lần kéo lại tính lại cả mật độ và marker của 6 tháng.
 */
export const ReplayTimeline = z.object({
    /** Event đầu tiên và cuối cùng của dự án; `null` khi chưa có event nào. */
    from: z.iso.datetime().nullable(),
    to: z.iso.datetime().nullable(),
    eventCount: z.number().int().nonnegative(),
    bucket: ReplayDensityBucket,
    density: z.array(ReplayDensityPoint),
    markers: z.array(ReplayMarker),
    /** Ô dày nhất — để phía vẽ chuẩn hoá chiều cao sparkline mà không quét lại. */
    peak: z.number().int().nonnegative(),
});
// ── Play mode: cuộn phim (APM-903a, spec 17.5) ──────────────────────────────
/**
 * Một event trong cuộn phim.
 *
 * Mang **cả payload** vì client phải tự gấp: xem `ReplayReel`.
 */
export const ReplayReelEvent = z.object({
    /** `event_log.id` dạng chuỗi — BigInt không qua JSON được nguyên vẹn. */
    id: z.string(),
    at: z.iso.datetime(),
    eventType: z.string(),
    aggregateType: z.string(),
    aggregateId: z.uuid(),
    /** Ai gây ra. `null` với event của hệ thống hoặc AI. */
    actorId: z.uuid().nullable(),
    payload: z.unknown(),
});
/**
 * Một đoạn phim để tua: **state đầu đoạn + event thô** — APM-903a, spec 17.5.
 *
 * ## Vì sao không trả state cho từng bước
 *
 * Play mode chạy vài chục bước mỗi giây. Một lời gọi `GET /replay?at=` cho mỗi
 * bước là 15–80ms cộng một vòng mạng — không thể mượt, và spec 17.5 nói rõ
 * "toàn bộ diễn ra phía client". Còn trả sẵn state cho N bước thì mỗi bước là
 * một bản state đầy đủ qua mạng, gấp nhiều lần so với gửi event thô.
 *
 * Nên: server gửi điểm xuất phát **một lần**, client gấp lũy tiến bằng **cùng
 * một hàm** (`@apm/replay`) mà server dùng.
 *
 * ## Vì sao `base` là `unknown`
 *
 * Hình dạng của nó là `ProjectState`, và chủ sở hữu hình dạng đó là
 * `@apm/replay` — không phải package này. Khai lại nó bằng Zod ở đây là tạo
 * **nguồn sự thật thứ hai** cho cùng một cấu trúc, đúng thứ CLAUDE.md cấm. Phía
 * web `as ProjectState` đúng một chỗ, ngay khi nhận.
 */
export const ReplayReel = z.object({
    /** Xuất xứ của `base`. `null` ở những trang tiếp theo (không có `base`). */
    meta: ReplayMeta.nullable(),
    /** `ProjectState` tại `from`; `null` khi đang tải trang tiếp. */
    base: z.unknown().nullable(),
    /** Event trong `(from, to]`, theo đúng thứ tự `(occurred_at, id)`. */
    events: z.array(ReplayReelEvent),
    /** Truyền lại ở `cursor` để lấy trang sau. `null` = đã hết đoạn. */
    nextCursor: z.string().nullable(),
});
/** Trần một trang cuộn phim. Client tải lũy tiến khi con trỏ tiến gần cuối. */
export const REEL_PAGE_LIMIT = 5_000;
export const REEL_PAGE_DEFAULT = 2_000;
// ── Thuyết minh (APM-903c, spec 17.6) ───────────────────────────────────────
/**
 * Một câu phụ đề, gắn với **một khoảng** thời gian.
 *
 * Khoảng, không phải một mốc: spec 17.6 đòi "gom nhóm nhiều event vụn thành
 * một câu có nghĩa". Một câu cho mỗi event là đúng thứ event feed đã làm, và
 * đọc nó ở tốc độ tua thì không ai theo được.
 */
export const ReplayNarrationCue = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    text: z.string().min(1).max(400),
});
/** Mốc đáng xem, dựng thành "lộ trình tua ngắn" — spec 17.6. */
export const ReplayHighlight = z.object({
    at: z.iso.datetime(),
    label: z.string().min(1).max(200),
    /** Vì sao mốc này đáng dừng lại. Không có thì người xem chỉ biết bấm theo. */
    why: z.string().min(1).max(400),
});
/**
 * Kết quả thuyết minh — **output của skill `narrate_replay`**.
 *
 * ## Vì sao `inflection` do model CHỌN, không do model ĐO
 *
 * Số liệu từng tuần (tiến độ, giờ còn lại, số task bị chặn, event nào đáng
 * chú ý) do code tính, tất định, và đi vào prompt. Model chỉ **chọn** tuần nào
 * là điểm bước ngoặt và **giải thích** vì sao. Cùng phân vai với `risk_scan`
 * (APM-702): code phát hiện, model diễn đạt — nhờ vậy con số trong lời thuyết
 * minh là con số hệ thống đo được, không phải con số model tưởng tượng.
 */
export const ReplayNarration = z.object({
    cues: z.array(ReplayNarrationCue).max(60),
    /**
     * Điểm dự án bắt đầu chệch khỏi kế hoạch, `null` khi không có.
     *
     * `null` là một câu trả lời hợp lệ và cần thiết: một dự án chạy đúng kế hoạch
     * mà bị gán "điểm bước ngoặt" sẽ khiến người đọc đi tìm một nguyên nhân không
     * tồn tại.
     */
    inflection: z
        .object({
        at: z.iso.datetime(),
        why: z.string().min(1).max(600),
    })
        .nullable(),
    highlights: z.array(ReplayHighlight).max(8),
});
/** Thuyết minh đã lưu cho một đoạn — `null` khi chưa sinh lần nào. */
export const ReplayNarrationView = z.object({
    suggestionId: z.uuid(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    narration: ReplayNarration,
    /** Chi phí lần sinh này, để người dùng biết tua lại không tốn thêm. */
    costUsd: z.string(),
    createdAt: z.iso.datetime(),
});
/**
 * Vỏ của endpoint đọc thuyết minh.
 *
 * ⚠️ Có vỏ chứ không trả thẳng `ReplayNarrationView | null`: Nest serialize
 * `null` thành **body rỗng**, và `apiFetch` đọc body rỗng thành `undefined` —
 * React Query thì từ chối `undefined` và cả query hỏng. "Chưa có bản nào" là
 * trạng thái thường gặp nhất của endpoint này, nên nó phải là một giá trị hợp
 * lệ, không phải một khoảng trống.
 */
export const ReplayNarrationResponse = z.object({
    narration: ReplayNarrationView.nullable(),
});
//# sourceMappingURL=replay.js.map